import { READ_AUDIT_LOG, type GrantablePermission, type PaidModule, type PermissionArea } from '@palaccount/contracts';

import { NAV_GROUPS } from '@/app/shell/nav';

/**
 * WHAT EACH ROUTE NEEDS — the client half of BR-PERM-51.
 *
 * The rule is worth quoting whole, because half of it is about this file and
 * the other half is about why this file is not a security control:
 *
 *   "Navigation filtering is a display convenience. Every route must enforce
 *    its own permission when it is opened, regardless of how the person
 *    arrived — menu, typed address, bookmark, notification link, or back
 *    button. A route must never rely on its menu entry being hidden."
 *
 * The SERVER has always done this: `PermissionGuard` annotates every handler
 * and refuses whatever the browser drew. What was missing was the second
 * sentence's consequence for the person: typing `/employees/payroll` rendered
 * the payroll screen, fired its reads, collected a 403 and showed a banner
 * inside a screen they may not open. PART 16.7 says what should happen
 * instead — "If they type the address, show the 'no permission' page" — and
 * BR-PERM-20 rules out the alternatives: "The system must never show a blank
 * screen, a spinner that never stops, or a technical error code when a
 * permission is missing."
 *
 * ── HOW A ROUTE'S REQUIREMENT IS DECIDED ───────────────────────────────────
 *
 * Longest matching prefix wins, and the fallback is the nav group's own areas
 * — so a route added inside an existing group inherits its group's gate rather
 * than needing somebody to remember. Only routes whose permission DIFFERS from
 * their menu group need an entry below, and each of those differences is a
 * spec statement rather than a build convenience.
 *
 * ── WHAT THIS FILE DELIBERATELY DOES NOT DO ────────────────────────────────
 *
 * It does not decide anything. It decides what to DRAW. Every route it admits
 * is still refused by the server if the server disagrees, and every route it
 * refuses is still refused by the server if the client is patched to skip it.
 * A build where this file were the check would be BR-PERM-50's exact defect.
 */
export interface RouteRequirement {
  /** Held-any-of. Empty means every session may open the route. */
  readonly areas: readonly PermissionArea[];
  /** Alternative grants that also open it (named extensions). */
  readonly extras?: readonly GrantablePermission[];
  /**
   * HELD-ALL-OF, **IN ADDITION** to `areas`/`extras` — the only CONJUNCTION in
   * this file, and the only field here that can make a route HARDER to open.
   *
   * ── WHY IT HAD TO EXIST ───────────────────────────────────────────────────
   *
   * `areas` and `extras` are both held-ANY-of, and they are OR'd with each
   * other. Until this field there was no way to say "and", so a route whose
   * server demands TWO permissions could only be described by one of them —
   * and either choice is wrong in a different direction:
   *
   *   name only `reports`     → a reader with `reports.view` alone opens a
   *                             screen whose service refuses it, and meets the
   *                             403 INSIDE a screen they may not open. That is
   *                             the defect BR-PERM-51 exists to prevent.
   *   name only `purchasing`  → a holder of `purchasing.view` without
   *                             `reports.view` is admitted and meets the
   *                             CONTROLLER's 403 instead. Same defect,
   *                             operands swapped.
   *
   * The reports centres are the first routes in the product with that shape:
   * `reports.controller.ts` annotates all nine routes `reports.view`, and each
   * centre demands its document area a second time INSIDE the service
   * (`reports.service.ts:311/745/1046/1152`,
   * `financial-statements.service.ts:141`). Two walls, both real.
   *
   * ── EXACT PERMISSIONS, NOT AREAS, AND THAT IS DELIBERATE ──────────────────
   *
   * `GrantablePermission` rather than `PermissionArea` because `holdsArea` is
   * "holds ANY permission in that area" — `purchasing.create` satisfies it —
   * whereas the service demands `purchasing.view` exactly. An area check here
   * would admit somebody the server then refuses, which is the whole failure
   * this field was added to remove.
   *
   * ── EVERY EXISTING ROUTE IS UNAFFECTED, STRUCTURALLY ──────────────────────
   *
   * The field is OPTIONAL and absent from every rule but the four report
   * centres, and the guard short-circuits `undefined` to `true`. So this is
   * not a semantics change to `areas` that each entry has to be re-read
   * against: an entry without `also` resolves exactly as it did before, and
   * that is a property of the type rather than a claim about the entries.
   *
   * A future reader reaching for this field: it TIGHTENS. It can never open a
   * route that `areas`/`extras` did not already open.
   */
  readonly also?: readonly GrantablePermission[];
  /** §1.5.3 — the company must hold the module. */
  readonly module?: PaidModule;
  /**
   * R18/BR-PERM-69 — the OWNER FLAG, which is not a permission and cannot be
   * expressed as one. ADM-07 is the only screen in the product with this
   * shape, and that is the point of it: "there is no switch that hands billing
   * to anybody, which is the point of it being a flag rather than a
   * permission" (§9).
   *
   * Like every field here it decides what to DRAW. The server refuses an
   * Accountant with `admin.view` inside the service, before it reads a row.
   */
  readonly ownerOnly?: boolean;
}

interface Rule extends RouteRequirement {
  readonly prefix: string;
}

/**
 * The exceptions. Ordered here for reading; matched by length, so the order in
 * this array does not decide anything.
 */
const EXCEPTIONS: readonly Rule[] = [
  /**
   * HR-02 Payroll. `payroll` is its own area (16.2.3), held by the Owner flag
   * alone until the Owner grants it (R15/R18, BR-PERM-39) — so an HR manager
   * with the full `hr` grid still may not open it, and the Employees group
   * they legitimately see must not become a door to salaries.
   */
  { prefix: '/employees/payroll', areas: ['payroll'] },
  /**
   * FIN-08 E-Invoicing. Its MENU home is Accounting & Finance (FIN-08 §3);
   * its PERMISSION area is `sales` — PART 16.2.2 row 1 names e-invoicing
   * there and FIN-08 §9 names it under accounting. The build follows 16.2.2
   * (router.tsx says so, and the acceptance report records it as spec defect
   * D-0); this entry is that same choice applied to the route guard, so the
   * two halves cannot answer differently.
   */
  { prefix: '/accounting/einvoicing', areas: ['sales'] },
  /**
   * DSH-09 Audit Log. Its menu home is the Reports group (DSH-09 §3, "the
   * fifth and last entry"), and `reports.view` does not open it: the read is
   * its own permission in the `admin` area (BR-DSH-09-13, BR-PERM-52).
   */
  { prefix: '/reports/audit', areas: [], extras: [READ_AUDIT_LOG] },

  // ── THE FOUR REPORT CENTRES (BR-PERM-51, BR-DSH-10-13) ───────────────────
  //
  // The MENU half of this was already right: the tabs are gated per centre.
  // The ROUTE half was not — none of these four had an entry, so all four
  // inherited the Reports group's coarse `reports` gate, and a reader holding
  // `reports.view` alone saw no tabs and could still open every one of them by
  // typing the address. BR-PERM-51's own sentence is about exactly that:
  // "Every route must enforce its own permission when it is opened, regardless
  // of how the person arrived — menu, typed address, bookmark…"
  //
  // EACH PERMISSION IS READ OFF THE SERVER, NOT INFERRED FROM THE ROUTE NAME.
  // `reports.controller.ts` carries `@RequirePermission('reports.view')` on
  // all nine routes (lines 25, 34, 43, 52, 61, 70, 79, 88, 97); the second
  // demand lives INSIDE the service and is invisible from the controller:
  //
  //   /reports            GET /api/reports/sales      reports.service.ts:311
  //                                                   demand('sales.view')
  //   /reports/purchases  GET /api/reports/purchases  :745  purchasing.view
  //                   AND GET .../price-trend         :1046 purchasing.view
  //   /reports/inventory  GET /api/reports/inventory  :1152 inventory.view
  //   /reports/financial  GET /api/reports/financial  financial-statements
  //                                                   .service.ts:141
  //                                                   holds('accounting.view')
  //
  // The purchases screen calls TWO endpoints — the report and the price-trend
  // card (`purchase-report-page.tsx:64` and `:87`) — and both demand
  // `purchasing.view`, so one grant covers the whole screen. A gate written
  // from route names would have missed the second endpoint entirely.
  //
  // `areas: ['reports']` is KEPT rather than replaced. It is the group's own
  // gate, it is what `shell-permissions.test.tsx`'s group loop reads, and
  // leaving it means these entries only ever ADD a condition. The tightening
  // is entirely in `also`.
  //
  // The group's `extras: [READ_AUDIT_LOG]` is deliberately NOT carried down:
  // it exists so the Reports GROUP appears for an audit-log holder, and it
  // should never have opened the sales report. `/reports/audit` above is where
  // that grant belongs, and it still has it.
  { prefix: '/reports/purchases', areas: ['reports'], also: ['reports.view', 'purchasing.view'] },
  { prefix: '/reports/inventory', areas: ['reports'], also: ['reports.view', 'inventory.view'] },
  { prefix: '/reports/financial', areas: ['reports'], also: ['reports.view', 'accounting.view'] },
  // CRM-03: GET /api/reports/statement demands `crm.view` inside the service.
  { prefix: '/reports/statement', areas: ['reports'], also: ['reports.view', 'crm.view'] },
  //
  // `/reports` is the Sales centre (DSH-05) and NOT a landing page — it is the
  // first tab. Its prefix therefore COLLIDES in length with the Reports nav
  // group's own rule, which is why the sort below now breaks ties toward the
  // exceptions explicitly instead of leaning on `Array.prototype.sort` being
  // stable. Relying on stability would have worked and would have been an
  // undocumented dependency on an engine detail in a security-shaped file.
  { prefix: '/reports', areas: ['reports'], also: ['reports.view', 'sales.view'] },
  /**
   * SAL-06 the till — the one authenticated route OUTSIDE the shell, so it
   * has no menu entry to be hidden and this is the only place the client can
   * say anything about it. Area `sales` (16.2.2 row 1).
   */
  { prefix: '/pos', areas: ['sales'] },
  /**
   * ADM-07 Billing. Its menu home is the Admin group, whose gate is
   * `admin` — and that gate is far too wide: 16.4.2 row 12 gives every
   * Accountant `R` on admin, and §2 says of the Administrator role itself
   * "NOTHING. This screen is not theirs." Owner ruling R18 moved billing to
   * the Owner flag alone (BR-ADM-07-09, BR-PERM-69), so the requirement is a
   * flag and not an area.
   */
  { prefix: '/admin/billing', areas: [], ownerOnly: true },
  /**
   * MD-09 Payment terms. Its menu home is General Settings (§3), whose gate
   * is `admin` — but §2 makes it the ACCOUNTANT's screen, and the API is
   * `accounting.*`. The Accountant holds admin R anyway; this entry is for
   * the custom role holding `accounting` without `admin`, whose server-side
   * access would otherwise be refused by the drawn-but-not-open route.
   */
  { prefix: '/settings/payment-terms', areas: ['accounting'] },

  // ── THE RUNG-4 FLOOR (PART 16.1, BR-PERM-73/-74) ─────────────────────────
  //
  // "A specific list of own-record capabilities, not a vague smallness." Three
  // of those own-record screens live INSIDE a menu group whose area the person
  // may hold nothing on, and the server says so already: every route behind
  // them is `@SelfService`, scoped to the caller, reachable with no role at
  // all. A guard that inherited the group's area would refuse a screen the
  // server admits — which is the failure this whole layer exists to prevent,
  // pointing the other way.
  //
  // THE ODDITY IS THE SPECIFICATION'S, and worth naming rather than smoothing
  // over: BR-PERM-43 hides EVERY menu group from a role-less user, so these
  // three screens are reachable and not findable. The spec's own answer is
  // that a personal home for them belongs to ADM-08/ADM-04, which are not
  // built. Until they are, the route works and the menu does not offer it.
  { prefix: '/employees/attendance', areas: [] },
  { prefix: '/employees/leave', areas: [] },
  { prefix: '/partners/followups', areas: [] },
];

/** Built once: the nav groups' own areas, as the default for their subtree. */
const GROUP_RULES: readonly Rule[] = NAV_GROUPS.filter((group) => group.to !== '/').map((group) => ({
  prefix: group.to,
  areas: group.areas,
  ...(group.extras ? { extras: group.extras } : {}),
  ...(group.module ? { module: group.module } : {}),
}));

/**
 * Longest prefix wins; an EXCEPTION wins a tie.
 *
 * The tiebreak became load-bearing when `/reports` gained an exception of its
 * own: it is the same string as the Reports nav group's prefix, so length
 * alone no longer separates them. Sorting by length only, the winner would be
 * whichever `Array.prototype.sort` happened to leave first — which is the
 * exception today, because the sort is stable and `EXCEPTIONS` is spread
 * first. That is TRUE and it is an engine guarantee doing a security-shaped
 * file's reasoning for it, so it is written down as a comparator instead.
 */
const RULES: readonly Rule[] = [
  ...EXCEPTIONS.map((rule) => ({ ...rule, isException: true })),
  ...GROUP_RULES.map((rule) => ({ ...rule, isException: false })),
]
  .slice()
  .sort((a, b) => b.prefix.length - a.prefix.length || Number(b.isException) - Number(a.isException));

/**
 * A path matches a rule when it IS the prefix or lies under it — `/salesforce`
 * must not inherit `/sales`, which a bare `startsWith` would give it.
 */
function matches(pathname: string, prefix: string): boolean {
  return pathname === prefix || pathname.startsWith(`${prefix}/`);
}

export function requirementFor(pathname: string): RouteRequirement {
  // '/' is the landing screen and belongs to everybody who has a session —
  // PART 16.7 puts the role-less user's explanation there (BR-PERM-73), which
  // needs them to be able to arrive.
  if (pathname === '/') return { areas: [] };
  const rule = RULES.find((candidate) => matches(pathname, candidate.prefix));
  // An unrouted path funnels to '/' before this is ever asked; a routed one
  // outside every group (there are none today) gets the session-only floor
  // rather than a refusal invented here — the server still decides.
  return rule ?? { areas: [] };
}
